import { AlertTriangle, Inbox, Loader2, Lock, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveFinish } from '../../platform.tsx';
import { ErrorBox } from '../ui.tsx';

export type StateKind = 'empty' | 'loading' | 'error' | 'denied';

const ICONS: Record<StateKind, LucideIcon> = {
  empty: Inbox,
  loading: Loader2,
  error: AlertTriangle,
  denied: Lock,
};

/**
 * Os quatro estados de toda tela (ADR 0128): vazia, carregando, com erro e sem permissão. O jeito
 * de desenhar vem do acabamento (`emptyStyle`): com ilustração, só o ícone ou só o texto. Quem
 * chama diz o que está vazio e o que fazer ("Nenhum cliente ainda", "Cadastrar cliente").
 */
export function StateView({
  kind,
  title,
  children,
  action,
  error,
  compact = false,
}: {
  kind: StateKind;
  /** Frase principal; sem ela, a padrão do estado. */
  title?: ReactNode;
  /** Explicação curta embaixo. */
  children?: ReactNode;
  /** O passo seguinte (um botão): cadastrar o primeiro, tentar de novo, pedir acesso. */
  action?: ReactNode;
  /** Com `kind="error"`, o erro da API, mostrado em linguagem simples. */
  error?: unknown;
  /** Dentro de um painel ou coluna: menos espaço em volta. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { emptyStyle } = useActiveFinish();
  const Icon = ICONS[kind];
  const tone =
    kind === 'error' ? 'text-danger' : kind === 'denied' ? 'text-muted' : 'text-brand-text';
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      aria-busy={kind === 'loading' || undefined}
      className={`flex flex-col items-center gap-2 text-center ${compact ? 'px-3 py-6' : 'px-4 py-12'}`}
    >
      {emptyStyle === 'illus' && (
        <span
          aria-hidden
          className={`mb-1 flex size-16 items-center justify-center rounded-full bg-paper ring-8 ring-paper/50 ${tone}`}
        >
          <Icon size={28} strokeWidth={1.5} className={kind === 'loading' ? 'animate-spin' : ''} />
        </span>
      )}
      {emptyStyle === 'icon' && (
        <Icon
          aria-hidden
          size={22}
          className={`${tone} ${kind === 'loading' ? 'animate-spin' : ''}`}
        />
      )}
      <p className="text-sm font-medium">{title ?? t(`patterns.state.${kind}`)}</p>
      {children && <div className="max-w-md text-sm text-muted">{children}</div>}
      {kind === 'error' && error != null && (
        <div className="w-full max-w-md text-left">
          <ErrorBox error={error} />
        </div>
      )}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}
